import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {useSelector} from "react-redux";
import {orderBy, sortBy} from "lodash";
import useMobile from "../../effects/use_mobile";
import useInterval from "@use-it/interval";
import classNames from "classnames";
import {VelocityComponent} from "velocity-react";
import {Hotspot} from "../hotspot";
import useWindowSize from "../../../service/use_window_size";
import useScrollbarVisibility from "../../../service/use_scrollbar_visibility";

export const DefaultImageMap = ({imageMap, onClick, ...props}) => {
  const hotspots = useSelector((state) => state.imageHotspots)
  const hotspotConfigs = useSelector((state) => state.imageHotspotConfigs)

  const filteredHotspots = hotspots.filter((hotspotConfig) => hotspotConfig.image_map_id === imageMap.id)
  const filteredHotspotConfigs = hotspotConfigs.filter((hotspotConfig) => hotspotConfig.image_map_config_id === imageMap.image_map_config_id)

  const sortedHotspots = orderBy(filteredHotspots, (hotspot) => hotspot.data.hotspot_order ? Number(hotspot.data.hotspot_order) : -1, ['desc']);
  const [hoverImage, setHoverImage] = useState(null)

  const imgEl = useRef()
  const refsArray = useRef([])
  const [imgLoaded, setImgLoaded] = useState(false)
  const [retryImageMapRerender, setRetryImageMapRerender] = useState(null)
  const windowSize = useWindowSize();
  const scrollbarVisibility = useScrollbarVisibility()
  const [elementDetails, setElementDetails] = useState({})

  const useMobileHotspots = useSelector(state => state.useMobileHotspots) !== false // So we can disable mobile hotspot rendering (needed for ringana)
  const {isMobileByMediaQuery, isMobileByUserAgent} = useMobile()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [imageMap.id])

  // So we changed this because, when hovering a hotspot the hotspots will be rendered.
  // This caused an issued e.g. for rendering photo booth inline because doka then always redraws the complete component.
  // Now, since if there is no hover image specified, this wont happen.
  // So there is still some potential for error if you use a inline html with e.g. photo booth and also have a hover image set.
  const onSetHoverHotspotId = useCallback((hoveredHotspotId) => {
    const hoverImage = hoveredHotspotId ? hotspots.find(hs => hs.id == hoveredHotspotId)?.hover_image?.url : null
    if (hoverImage) {
      setHoverImage(hoverImage)
    }
  }, [hotspots, setHoverImage])

  const renderMap = () => {
    if (isMobileByMediaQuery) {
      console.log("Not rendering hotspots due to mobile rendering")
      if (retryImageMapRerender) {
        setRetryImageMapRerender(null);
      }
      return
    }

    // No background image for render_mode html_content
    if (imageMap.render_mode === "html_content") {
      return
    }

    //console.log("renderMap", imgEl?.current, filteredHotspots, filteredHotspotConfigs, imgEl?.current, imgEl?.current?.width, imgEl?.current?.height)

    if (imgEl?.current?.width > 0 && imgEl?.current?.height > 0) {
      let newDimensions = {}
      filteredHotspots.map((hotspot) => {
        const hotspotConfig = filteredHotspotConfigs.find(hc => hotspot.image_hotspot_config_id === hc.id)
        newDimensions[hotspot.id] = {
          left: imgEl.current.width * (hotspotConfig.position_left / 100),
          top: imgEl.current.height * (hotspotConfig.position_top / 100),
          width: imgEl.current.width * (hotspotConfig.element_width / 100),
          height: imgEl.current.height * (hotspotConfig.element_height / 100),
        }
      })
      setElementDetails(newDimensions)
      setRetryImageMapRerender(null)
    } else {
      console.debug("Not ready")
      if (retryImageMapRerender == null)
        setRetryImageMapRerender(250)
    }
  }

  // wait until images have rendered to measure its size for the hotspot position
  useInterval(renderMap, retryImageMapRerender)

  useLayoutEffect(renderMap, [
    imageMap?.id,
    imageMap?.render_mode,
    imgEl?.current?.width,
    imgEl?.current?.height,
    refsArray,
    imgLoaded,
    windowSize,
    scrollbarVisibility,
    hotspots,
    hotspotConfigs
  ])

  let positionSortedHotspots = sortedHotspots

  if (!(isMobileByUserAgent || isMobileByMediaQuery)) {
    const filteredSortedHotspots = sortedHotspots.filter((hotspot) => (props.showHiddenHotspots || hotspot.kind !== "hidden"))
    positionSortedHotspots = sortBy(filteredSortedHotspots, [(hotspot) => {
      return parseFloat(filteredHotspotConfigs.find(hc => hotspot.image_hotspot_config_id === hc.id)?.position_left)
    }])
  }

  return <>
    {(!useMobileHotspots || !isMobileByMediaQuery) && <img ref={imgEl}
                                                           id="image-map-background-image"
                                                           className={classNames("mobile-background-image", "mobile-background-image" + imageMap.id)}
                                                           src={imageMap.background_image_url}
                                                           style={{width: "100%"}}
                                                           onLoad={() => {
                                                             setImgLoaded(true)
                                                           }}/>}
    {(!useMobileHotspots || !isMobileByMediaQuery) && hoverImage &&
      <img
        className={"image-map-hover-image"}
        src={hoverImage}
        style={{width: "100%", position: "absolute", top: 0, left: 0}}
      />}
    {(useMobileHotspots && isMobileByMediaQuery) && <img
      className={classNames("mobile-background-mobile-image", "mobile-background-mobile-image" + imageMap.id)}
      src={imageMap.mobile_background_image_url || imageMap.background_image_url}
      style={{width: "100%"}}
      onLoad={() => {
        setImgLoaded(true)
      }}/>}

    {imgLoaded &&
      <>
        {positionSortedHotspots.map((hotspot, index) => {
            const hotspotConfig = filteredHotspotConfigs.find(hc => hotspot.image_hotspot_config_id === hc.id)
            const delay = (1250 / positionSortedHotspots.length) * index
            //console.log("delay", delay)
            //console.log({hotspotConfig})

            return <VelocityComponent
              key={"vlc" + hotspot.id}
              animation={{opacity: [1,0]}}
              loop={false}
              runOnMount={true}
              delay={delay} duration={500}>
              <div style={{opacity: 0}}>
                <Hotspot
                  key={"hotspot" + hotspot.id}
                  hotspot={hotspot}
                  hotspotConfig={hotspotConfig}
                  refsArray={refsArray}
                  isEventBooth={props.isEventBooth}
                  onHotspotClick={props.onHotspotClick}
                  dotted={props.dotted}
                  showHiddenHotspots={props.showHiddenHotspots}
                  setHoveredHotspotId={onSetHoverHotspotId}
                  elementDetails={elementDetails}/>
              </div>
            </VelocityComponent>
          }
        )}
      </>
    }
  </>
}
